import { createContext, useContext, useState, useEffect, useCallback } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem("padel_access_token");
    if (!token) {
      setUser(false);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      localStorage.removeItem("padel_access_token");
      localStorage.removeItem("padel_refresh_token");
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("padel_access_token", data.access_token);
    localStorage.setItem("padel_refresh_token", data.refresh_token);
    const { access_token, refresh_token, ...userData } = data;
    setUser(userData);
    return userData;
  };

  const register = async (email, password, name, referral_code, phone) => {
    const payload = { email, password, name };
    if (referral_code) payload.referral_code = referral_code;
    if (phone) payload.phone = phone;
    const { data } = await api.post("/auth/register", payload);
    localStorage.setItem("padel_access_token", data.access_token);
    localStorage.setItem("padel_refresh_token", data.refresh_token);
    const { access_token, refresh_token, ...userData } = data;
    setUser(userData);
    return userData;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (err) { console.warn("Logout request failed", err); }
    localStorage.removeItem("padel_access_token");
    localStorage.removeItem("padel_refresh_token");
    setUser(false);
  };

  const refreshUser = async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (err) {
      console.warn("Could not refresh user", err);
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
